export const PLATFORM_NAMES = { kdp: "Amazon KDP", ingram: "IngramSpark", website: "Website" };
export function formatName(value) {
  const normalized = String(value || "").toLowerCase();
  return ({ ebook: "eBook", digital: "eBook", paperback: "Paperback", hardcover: "Hardcover", merch: "Merchandise" })[normalized] || value || "Unknown";
}
export function kdpFormats(records) {
  const formats = new Map();
  records.filter(r => r.kind === "royalty").forEach(r => {
    const previous = formats.get(r.identifier);
    formats.set(r.identifier, previous && previous !== r.format ? "Unknown" : r.format);
  });
  return records.map(r => ({ ...r, format: formatName(r.format || formats.get(r.identifier)) }));
}
export function bookLookup(catalog = []) {
  const identifiers = new Map(); const titles = new Map();
  const normalize = value => String(value || "").replace(/[\s-]/g, "").toUpperCase();
  for (const book of catalog) {
    const title = String(book.title || "").trim(); if (!title) continue;
    titles.set(title.toLowerCase(), title);
    for (const field of ["asin", "ebook_isbn", "paperback_isbn", "hardcover_isbn"]) {
      const id = normalize(book[field]);
      if (!/^([A-Z0-9]{10}|\d{13})$/.test(id)) continue;
      identifiers.set(id, identifiers.has(id) && identifiers.get(id) !== title ? null : title);
    }
  }
  return (title, identifier) => identifiers.get(normalize(identifier)) || titles.get(String(title || "").trim().toLowerCase()) || String(title || "Unknown book").trim();
}
export function salesRows(kdp, reports, orders, compensation = [], legacy = [], catalog = []) {
  const titleFor = bookLookup(catalog);
  const rows = kdpFormats(kdp).filter(r => r.kind === "royalty").map(r => ({
    id: `kdp-${r.id}`, platform: "kdp", category: "book", title: titleFor(r.book_title, r.identifier), format: r.format,
    start: r.report_date.slice(0, 10), end: r.report_date.slice(0, 10), units: Number(r.units_sold) - Number(r.units_refunded),
    refunds: Number(r.units_refunded), amount: r.royalty_amount == null ? null : Number(r.royalty_amount), currency: r.currency, amount_kind: "Estimated royalty", market: r.marketplace,
  }));
  reports.forEach(report => report.records.forEach((r, i) => rows.push({
    id: `ingram-${report.id}-${i}`, platform: "ingram", category: "book", title: titleFor(r.book_title, r.isbn), format: formatName(r.format),
    start: report.start, end: report.end, units: Number(r.units_sold), refunds: 0, amount: null, currency: null, amount_kind: "Unavailable", market: r.country,
  })));
  compensation.forEach(report => report.records.forEach((row, index) => rows.push({
    id: `ingram-comp-${report.id}-${index}`, platform: "ingram", category: "book", title: titleFor(row.book_title, row.isbn),
    format: formatName(row.format), start: report.start, end: report.end, units: Number(row.net_units || 0),
    count_units: !ingramUnitsCovered(reports, report, row) && !legacy.some(entry => ["ingram", "ingramspark"].includes(String(entry.platform).toLowerCase()) && entry.sale_date <= report.end && (entry.period_end || entry.sale_date) >= report.start),
    refunds: Number(row.units_refunded || 0), amount: Number(row.compensation), currency: row.currency,
    amount_kind: "Earned compensation", market: row.market,
  })));
  legacy.forEach(row => {
    const platform = ({ amazon: "kdp", kdp: "kdp", ingram: "ingram", ingramspark: "ingram" })[String(row.platform || "").toLowerCase()];
    if (!platform || !row.sale_date) return;
    rows.push({ id: `legacy-${row.id}`, platform, category: "book", title: titleFor(row.book_title, row.asin_or_isbn), format: formatName(row.format),
      start: String(row.sale_date).slice(0, 10), end: String(row.period_end || row.sale_date).slice(0, 10), units: Number(row.units_sold || 0) - Number(row.units_refunded || 0),
      refunds: Number(row.units_refunded || 0), amount: row.royalty_amount == null ? null : Number(row.royalty_amount), currency: row.currency || "Unknown",
      amount_kind: "Reported royalty", market: row.country || "Unknown",
    });
  });
  orders.forEach(order => {
    const day = String(order.created_at || "").slice(0, 10);
    if (order.type === "book" && order.payment_status === "paid") rows.push({
      id: `website-book-${order.id}`, platform: "website", category: "book", title: titleFor(order.book_title), format: formatName(order.format), start: day, end: day,
      units: Number(order.quantity || 1), refunds: null, amount: order.price_paid == null ? null : Number(order.price_paid) * Number(order.quantity || 1), currency: order.currency || "USD", amount_kind: "Sales revenue", market: "Website",
    });
    if (order.type === "merch" && ["submitted", "resubmitted"].includes(order.fulfillment_status)) (order.items || []).forEach((item, i) => rows.push({
      id: `website-merch-${order.id}-${i}`, platform: "website", category: "merch", title: item.title || item.name || "Merchandise", format: "Merchandise", start: day, end: day,
      units: Number(item.quantity || item.qty || 1), refunds: null, amount: item.price == null ? null : Number(item.price) * Number(item.quantity || item.qty || 1), currency: order.currency || "USD", amount_kind: "Sales revenue", market: "Website",
    }));
  });
  return rows.sort((a, b) => b.end.localeCompare(a.end) || a.title.localeCompare(b.title));
}
export function ingramUnitsCovered(reports, report, record) {
  return reports.some(unitReport => (unitReport.start <= report.end && unitReport.end >= report.start) || (record.transaction_date && unitReport.start <= record.transaction_date && unitReport.end >= record.transaction_date));
}
export function reportingBounds(period, year, month, start, end) {
  if (period === "all") return null;
  if (period === "range") return start && end && start <= end ? [start, end] : [];
  if (period === "year") return [`${year}-01-01`, `${year}-12-31`];
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  return [`${prefix}-01`, `${prefix}-${new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate()}`];
}
export function filterSales(rows, bounds, search = "", format = "") {
  const query = search.trim().toLowerCase();
  return rows.filter(r => (!query || `${r.title} ${PLATFORM_NAMES[r.platform]} ${r.market}`.toLowerCase().includes(query)) && (!format || r.format === format))
    .filter(r => bounds === null || (bounds.length === 2 && r.start <= bounds[1] && r.end >= bounds[0]))
    .map(r => ({ ...r, included: bounds === null || (r.start >= bounds[0] && r.end <= bounds[1]) }));
}
export function totalSales(rows) {
  const totals = { book_units: 0, merch_units: 0, excluded: 0, amounts: {} };
  rows.forEach(r => {
    if (!r.included) { totals.excluded += 1; return; }
    if (r.count_units !== false) totals[r.category === "merch" ? "merch_units" : "book_units"] += r.units;
    if (r.amount !== null) {
      const key = `${r.amount_kind}|${r.currency}`;
      totals.amounts[key] = (totals.amounts[key] || 0) + Math.round(r.amount * 1000000);
    }
  });
  totals.amounts = Object.fromEntries(Object.entries(totals.amounts).map(([key, amount]) => [key, amount / 1000000]));
  return totals;
}

export function bestSellingBooks(rows) {
  const books = new Map();
  rows.filter(row => row.included && row.category === "book" && row.count_units !== false).forEach(row => {
    books.set(row.title, (books.get(row.title) || 0) + row.units);
  });
  const ranked = [...books].map(([title, units]) => ({ title, units })).filter(book => book.units > 0).sort((a, b) => b.units - a.units || a.title.localeCompare(b.title));
  return ranked.filter(book => book.units === ranked[0]?.units);
}
